import React, { useState } from "react"
import PropTypes from "prop-types"
import { useTable, useSortBy } from "react-table"
import {
  Container,
  Table as StyledTable,
  Thead,
  Tr,
  Th,
  ThWrap,
  Tbody,
  Td,
  SearchBox,
  MyInterviewsToggle,
  MyInterviewsToggleText
} from "./styles"
import Grid from "../grid"
import Level from "../level"
import P from "../typography/p"
import Filters from "./filters"
import Columnsets from "./columnsets"
import Actions from "./actions"
import {
  PlainCell,
  BooleanCell,
  ImageCell,
  DateCell,
  DateRangeCell,
  DateTimeCell,
  ExternalLinkCell,
  DocumentCell,
  ActionsCell,
  DropdownActionsCell,
  SelectionCell,
  TernaryCell,
  RatingFieldCell,
  RatingBoolFieldCell,
  ViewProfileFieldCell,
  InvoiceStatusCell,
  EmployerUserNameCell,
  StatusCell
} from "./cells"
import moment from "moment-timezone"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSort, faSortDown, faSortUp } from "@fortawesome/pro-solid-svg-icons"
import { SwitchField } from "../forms"

const FILTERABLE_COLUMN_TYPES = ["plain", "view_profile", "date", "datetime"]

const cellTypes = {
  plain: PlainCell,
  currency: PlainCell,
  boolean: BooleanCell,
  image: ImageCell,
  date: DateCell,
  datetime: DateTimeCell,
  date_range: DateRangeCell,
  external_link: ExternalLinkCell,
  document: DocumentCell,
  actions: ActionsCell,
  dropdown_actions: DropdownActionsCell,
  selection: SelectionCell,
  ternary: TernaryCell,
  rating_field: RatingFieldCell,
  bool_field: RatingBoolFieldCell,
  view_profile: ViewProfileFieldCell,
  invoice_status: InvoiceStatusCell,
  employer_user_name: EmployerUserNameCell,
  status: StatusCell,
  reference: DocumentCell
}

const Table = ({
  columns,
  filters,
  actions,
  data,
  gridArea,
  noneText,
  actionHandler,
  filterHandler,
  actionCellHandler,
  viewProfileCellHandler,
  q,
  columnsets,
  selectedColumnset,
  setColumnset,
  documentHandler,
  csvData,
  csvHeaders,
  selectionHandler,
  selectedRows,
  clearFilterHandler,
  clearAllFiltersHandler,
  showSearchBox,
  showMyInterviewsToggle,
  handleMyInterviewsToggle,
  myInterviewsCount,
  myInterviewsOnly,
  showCountsummary,
  simpleForm
}) => {
  const [filteredData, setFilteredData] = useState(data)
  const [searchValue, setSearchValue] = useState("")

  if (selectionHandler !== undefined && !columns.some((c) => c.type === "selection")) {
    columns.unshift({ accessor: "selection", type: "selection" })
  }

  const selectAll = (event) => {
    event.stopPropagation()
    const { checked } = event.target
    if (selectionHandler !== undefined) selectionHandler("all", checked)
  }

  columns.forEach((column) => {
    if (!column.type) column.type = "plain"
    column.Cell = cellTypes[column.type]

    if (column.type == "boolean" || column.type == "ternary" || column.type == "invoice_status") {
      column.sortType = "basic"
    } else if (column.type === "currency") {
      column.sortType = (a, b) => {
        let valueA = parseFloat(a.values[column.accessor].replace(/[^0-9.-]+/g, ""))
        let valueB = parseFloat(b.values[column.accessor].replace(/[^0-9.-]+/g, ""))
        if (valueA < valueB) return -1
        else if (valueB < valueA) return 1
        else return 0
      }
    } else if (column.type === "date" || column.type === "datetime") {
      column.sortType = (a, b) => {
        let dateA = moment(a.values[column.accessor])
        let dateB = moment(b.values[column.accessor])
        if (!dateA.isValid() || dateA.isBefore(dateB)) return -1
        else if (dateB.isBefore(dateA)) return 1
        else return 0
      }
    } else if (column.type === "date_range") {
      column.sortType = (a, b) => {
        let dateA = moment(a.values[column.accessor][0])
        let dateB = moment(b.values[column.accessor][0])
        if (!dateA.isValid() || dateA.isBefore(dateB)) return -1
        else if (dateB.isBefore(dateA)) return 1
        else return 0
      }
    } else if (["actions", "selection"].includes(column.type)) {
      column.disableSortBy = true
    }

    if (column.type === "selection") {
      column.Header = <input type="checkbox" onChange={selectAll} />
    }
  })

  const tableInstance = useTable({ columns, data: filteredData }, useSortBy)

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance

  const prepareValueForSearch = (type, value) => {
    if (type === "date") return moment(value).format("DD MMMM YYYY").toLowerCase()
    else if (type === "datetime") return moment(value).format("DD MMMM YYYY HH:mm").toLowerCase()
    else return value.toString().toLowerCase()
  }

  const filterableColumns = columns.filter((column) => FILTERABLE_COLUMN_TYPES.includes(column.type))

  const handleSearchBox = (event) => {
    const { value } = event.target
    setSearchValue(value)

    if (!Array.isArray(data)) return

    let filteredData = []

    filteredData = data.filter((row) =>
      filterableColumns.some((column) => {
        let datum = row[column.accessor]
        if (datum === undefined || datum === null) return false
        return prepareValueForSearch(column.type, datum).includes(value.toString().toLowerCase())
      })
    )

    setFilteredData(filteredData)
  }

  const handleFilter = (name, value) => {
    setSearchValue("")
    setFilteredData(data)
    if (filterHandler !== undefined) filterHandler(name, value)
  }

  const renderSortIcon = ({ disableSortBy, isSorted, isSortedDesc }) => {
    if (!!disableSortBy) return null
    else if (!isSorted) return <FontAwesomeIcon icon={faSort} size="sm" />
    else if (!!isSortedDesc) return <FontAwesomeIcon icon={faSortDown} size="sm" />
    else return <FontAwesomeIcon icon={faSortUp} size="sm" />
  }

  return (
    <Grid gap="1rem" gridArea={gridArea}>
      <Filters
        filters={filters}
        filterHandler={handleFilter}
        q={q}
        clearFilterHandler={clearFilterHandler}
        clearAllFiltersHandler={clearAllFiltersHandler}
      />
      {((columnsets && columnsets.length > 0) ||
        (actions && actions.length > 0) ||
        showSearchBox ||
        showMyInterviewsToggle) && (
        <Level mobile={false}>
          {showMyInterviewsToggle && myInterviewsCount > 0 && (
            <MyInterviewsToggle>
              <SwitchField
                name="showMyInterviews"
                handleInputChange={handleMyInterviewsToggle}
                checked={myInterviewsOnly}
              />
              <MyInterviewsToggleText>Only show my interviews ({myInterviewsCount})</MyInterviewsToggleText>
            </MyInterviewsToggle>
          )}

          <Columnsets columnsets={columnsets} selected={selectedColumnset} setSelected={setColumnset} />
          <Grid template="auto / 2fr 1fr" placeItems="start end">
            <Actions actions={actions} actionHandler={actionHandler} csvData={csvData} />
            {!!showSearchBox && (
              <SearchBox name="text_search" value={searchValue} placeholder="Search" onChange={handleSearchBox} />
            )}
          </Grid>
        </Level>
      )}
      <Container>
        <StyledTable {...getTableProps()} simpleForm={simpleForm}>
          <Thead simpleForm={simpleForm}>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) =>
                  column.wrap_column ? (
                    <ThWrap {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render("Header")} {renderSortIcon(column)}
                    </ThWrap>
                  ) : (
                    <Th {...column.getHeaderProps(column.getSortByToggleProps())} simpleForm={simpleForm}>
                      {column.render("Header")} {renderSortIcon(column)}
                    </Th>
                  )
                )}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()} simpleForm={simpleForm}>
            {rows.length > 0
              ? rows.map((row) => {
                  prepareRow(row)

                  return (
                    <Tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <Td {...cell.getCellProps()} simpleForm={simpleForm} type={cell.column.type}>
                          {cell.render("Cell", {
                            actionCellHandler,
                            viewProfileCellHandler,
                            documentHandler,
                            selectionHandler,
                            selectedRows,
                            simpleForm,
                            type: cell.column.type
                          })}
                        </Td>
                      ))}
                    </Tr>
                  )
                })
              : columns.length > 0 && (
                  <Tr>
                    <Td colSpan={columns.length} centered simpleForm={simpleForm}>
                      {noneText}
                    </Td>
                  </Tr>
                )}
          </Tbody>
        </StyledTable>
      </Container>
      {!!showCountsummary && (
        <P variant="content" align="center">
          Showing {filteredData.length} of {data.length}
        </P>
      )}
    </Grid>
  )
}

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  filters: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.arrayOf(PropTypes.object),
  gridArea: PropTypes.string,
  noneText: PropTypes.string,
  actionHandler: PropTypes.func,
  filterHandler: PropTypes.func,
  actionCellHandler: PropTypes.func,
  viewProfileCellHandler: PropTypes.func,
  q: PropTypes.object,
  columnsets: PropTypes.array,
  selectedColumnset: PropTypes.string,
  setColumnset: PropTypes.func,
  documentHandler: PropTypes.func,
  selectionHandler: PropTypes.func,
  selectedRows: PropTypes.array,
  clearFilterHandler: PropTypes.func,
  clearAllFiltersHandler: PropTypes.func,
  showSearchBox: PropTypes.bool,
  showCountsummary: PropTypes.bool
}

Table.defaultProps = {
  columns: [],
  data: [],
  filters: [],
  actions: [],
  gridArea: undefined,
  noneText: "No records.",
  actionHandler: undefined,
  filterHandler: undefined,
  actionCellHandler: undefined,
  viewProfileCellHandler: undefined,
  q: {},
  columnsets: [],
  selectedColumnset: "",
  setColumnset: undefined,
  documentHandler: undefined,
  selectionHandler: undefined,
  selectedRows: [],
  clearFilterHandler: undefined,
  clearAllFiltersHandler: undefined,
  showSearchBox: false,
  showCountsummary: true
}

export default Table
